import * as Sentry from '@sentry/react';
import { initSentry } from '@wavvest/shared-services';

// Environment detection using Vite's import.meta.env
const isProd = import.meta.env.PROD;
const forceSentry = import.meta.env.VITE_FORCE_SENTRY === 'true';
const isScreenshotGallery =
  import.meta.env.VITE_SCREENSHOT_GALLERY === '1' ||
  import.meta.env.VITE_SCREENSHOT_GALLERY === 'true';
const SCREENSHOT_GALLERY_SENTRY_CAPTURE_STORAGE_KEY = 'wavvest:screenshot-gallery:sentry-capture';
type ScreenshotGallerySentryCaptureMode = 'throw' | 'capture' | 'off';

const parseScreenshotGallerySentryCaptureMode = (
  value: string | null,
): ScreenshotGallerySentryCaptureMode | null => {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'throw' || normalized === 'capture' || normalized === 'off') {
    return normalized;
  }
  return null;
};

// DSN from environment variable
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export const resolveSentryEnvironment = ({
  mode,
  configuredEnvironment,
  isProdBuild,
}: {
  mode?: string;
  configuredEnvironment?: string;
  isProdBuild: boolean;
}): string => {
  const normalizedEnvironment = configuredEnvironment?.trim().toLowerCase();
  if (normalizedEnvironment === 'prod' || normalizedEnvironment === 'production') return 'prod';
  if (normalizedEnvironment === 'stg' || normalizedEnvironment === 'staging') return 'stg';
  if (normalizedEnvironment === 'dev' || normalizedEnvironment === 'development') return 'dev';

  const normalizedMode = mode?.trim().toLowerCase();
  if (normalizedMode === 'production' || normalizedMode === 'prod') return 'prod';
  if (normalizedMode === 'staging' || normalizedMode === 'stg') return 'stg';
  if (normalizedMode === 'dev' || normalizedMode === 'development') return 'dev';

  return isProdBuild ? 'prod' : 'dev';
};

const readScreenshotGallerySentryCaptureMode = (): ScreenshotGallerySentryCaptureMode | null => {
  if (!isScreenshotGallery || typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const queryValue = parseScreenshotGallerySentryCaptureMode(
    searchParams.get('sentryCapture') ||
      searchParams.get('screenshotSentryCapture') ||
      searchParams.get('sgSentryCapture'),
  );
  if (queryValue) {
    try {
      window.localStorage.setItem(SCREENSHOT_GALLERY_SENTRY_CAPTURE_STORAGE_KEY, queryValue);
    } catch {
      // Gallery seams are best-effort and must never affect production flows.
    }
    return queryValue;
  }

  try {
    return parseScreenshotGallerySentryCaptureMode(
      window.localStorage.getItem(SCREENSHOT_GALLERY_SENTRY_CAPTURE_STORAGE_KEY),
    );
  } catch {
    return null;
  }
};

const shouldThrowSentryCaptureForScreenshotGallery = (): boolean =>
  readScreenshotGallerySentryCaptureMode() === 'throw';

export function initializeSentry() {
  initSentry({
    dsn: SENTRY_DSN,
    isProd,
    forceSentry,
    environment: resolveSentryEnvironment({
      mode: import.meta.env.MODE,
      configuredEnvironment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      isProdBuild: isProd,
    }),
    release: `wavvest-weave@${import.meta.env.VITE_APP_VERSION || 'unknown'}`,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Set user context when authenticated
export function setSentryUser(user: { id?: string; username?: string }) {
  Sentry.setUser({
    id: user.id,
    username: user.username,
  });
}

// Clear user context on logout
export function clearSentryUser() {
  Sentry.setUser(null);
}

// Add custom tags
export function setSentryTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

// Capture exceptions with context
export function captureException(error: Error, context?: Record<string, unknown>): string {
  if (shouldThrowSentryCaptureForScreenshotGallery()) {
    throw new Error('Screenshot gallery forced Sentry capture failure');
  }

  return Sentry.captureException(error, {
    extra: context,
  });
}

// Capture messages
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>,
): string {
  return Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

// Re-export ErrorBoundary for use in components
export const SentryErrorBoundary = Sentry.ErrorBoundary;
